import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InstallerLogo, appConfig } from "@/branding/Branding";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatDayLabel(d) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (isSameDay(d, today)) return "Today";
    if (isSameDay(d, yesterday)) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}
function formatTime(iso) {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

/**
 * Full-screen customer ↔ installer chat. Web port of the mobile ChatThread —
 * inside CustomerShell it fills the pane edge-to-edge with day dividers, avatar
 * bubbles for the other party, sticky composer, empty state and 5s polling.
 * Installer role reaches this via shared /messages and picks the first project.
 */
export default function Messages() {
    const { user } = useAuth();
    const [projectId, setProjectId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const initialScrollDone = useRef(false);
    const pollRef = useRef(null);

    // Resolve the project once, then start polling for messages.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const p = user?.role === "installer"
                    ? (await api.listProjects())[0]
                    : await api.myProject();
                if (!p) throw new Error("No project");
                if (!alive) return;
                setProjectId(p.id);
            } catch {
                if (alive) toast.error("Could not load messages");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [user]);

    const pull = useCallback(async () => {
        if (!projectId) return;
        try {
            const m = await api.listMessages(projectId);
            setMessages(m);
        } catch { /* stay quiet on transient polling errors */ }
    }, [projectId]);

    useEffect(() => {
        if (!projectId) return;
        pull();
        pollRef.current = setInterval(pull, 5000);
        return () => clearInterval(pollRef.current);
    }, [projectId, pull]);

    // Jump to bottom on first paint, animate on subsequent updates.
    useEffect(() => {
        if (!scrollRef.current || messages.length === 0) return;
        const el = scrollRef.current;
        const behavior = initialScrollDone.current ? "smooth" : "auto";
        el.scrollTo({ top: el.scrollHeight, behavior });
        initialScrollDone.current = true;
    }, [messages]);

    // Group messages into day-divider rows + message rows.
    const rows = useMemo(() => {
        const out = [];
        let lastKey = null;
        for (const m of messages) {
            let d;
            try { d = new Date(m.created_at); } catch { d = new Date(); }
            const dayKey = d.toDateString();
            if (dayKey !== lastKey) {
                out.push({ type: "day", key: `d-${dayKey}`, label: formatDayLabel(d) });
                lastKey = dayKey;
            }
            out.push({ type: "msg", key: m.id, msg: m });
        }
        return out;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault();
        const value = text.trim();
        if (!value || !projectId || sending) return;
        setSending(true);
        try {
            const saved = await api.sendMessage(projectId, value);
            setMessages((prev) => [...prev, saved]);
            setText("");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Message failed");
        } finally {
            setSending(false);
        }
    };

    const backTo = user?.role === "installer" ? "/installer" : "/dashboard";

    return (
        <div className="h-screen md:h-screen flex flex-col bg-[#F5F5F7]" data-testid="messages-page">
            {/* DEV-022 · Customer Messages premium header —
                icon-tile + eyebrow treatment matching the rest of
                the redesigned surfaces. */}
            <div className="shrink-0 px-4 md:px-8 pt-4 md:pt-6 pb-4 border-b border-[#E5E7EB] bg-white">
                <BackButton fallbackHref={backTo} testid="messages-back" className="mb-3" />
                <div className="flex items-center gap-3">
                    <span aria-hidden className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <MessageSquare size={18} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                            Customer Portal · Messages
                        </div>
                        <h1 className="text-xl md:text-2xl text-[#131B2F] font-semibold leading-tight">
                            Chat with your installer team
                        </h1>
                    </div>
                </div>
            </div>

            {/* Scrolling thread — fills all remaining vertical space */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6"
                data-testid="messages-scroll"
            >
                {loading ? (
                    <div className="h-full flex items-center justify-center text-sm text-[#4B5565]">Loading conversation…</div>
                ) : rows.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center py-12 gap-2" data-testid="messages-empty">
                        <div className="w-14 h-14 rounded-full bg-[#3E9E2E]/12 flex items-center justify-center">
                            <MessageSquare size={22} strokeWidth={1.75} className="text-[#3E9E2E]" />
                        </div>
                        <div className="text-base text-[#131B2F] font-medium mt-1">No messages yet</div>
                        <p className="text-sm text-[#4B5565] text-center max-w-xs">Send the first message to start the conversation with your installer.</p>
                    </div>
                ) : (
                    <ol className="mx-auto max-w-3xl space-y-2.5">
                        {rows.map((row) => {
                            if (row.type === "day") {
                                return (
                                    <li key={row.key} className="flex items-center gap-3 my-4" data-testid="messages-day-divider">
                                        <div className="flex-1 h-px bg-[#E5E7EB]" />
                                        <span className="text-[11px] tracking-[0.08em] text-[#4B5565] font-medium uppercase">{row.label}</span>
                                        <div className="flex-1 h-px bg-[#E5E7EB]" />
                                    </li>
                                );
                            }
                            const m = row.msg;
                            const mine = m.sender_id === user?.id;
                            const initial = (m.sender_name || "?").charAt(0).toUpperCase();
                            return (
                                <li key={row.key} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                                    {!mine && (
                                        <div className="w-7 h-7 rounded-full bg-[#3E9E2E]/16 text-[#3E9E2E] text-xs font-medium flex items-center justify-center shrink-0">
                                            {initial}
                                        </div>
                                    )}
                                    <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine
                                        ? "bg-[#3E9E2E] text-white rounded-br-sm"
                                        : "bg-white text-[#131B2F] rounded-bl-sm border border-[#E5E7EB]"}`}>
                                        {!mine && <div className="text-[11px] font-medium text-[#4B5565] mb-0.5">{m.sender_name}</div>}
                                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                        <div className={`text-[10px] mt-1 text-right ${mine ? "text-white/75" : "text-[#4B5565]"}`}>{formatTime(m.created_at)}</div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            {/* Sticky composer */}
            <form onSubmit={send} className="shrink-0 bg-white border-t border-[#E5E7EB] px-4 md:px-8 py-3 md:py-4" data-testid="messages-form">
                <div className="mx-auto max-w-3xl flex items-end gap-2">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                        placeholder="Type a message…"
                        rows={1}
                        className="flex-1 min-h-[42px] max-h-32 px-4 py-2.5 rounded-2xl border border-[#E5E7EB] bg-[#F5F5F7] resize-none text-base text-[#131B2F] outline-none focus:border-[#3E9E2E] focus:bg-white transition-colors"
                        data-testid="messages-input"
                    />
                    <button
                        type="submit"
                        disabled={sending || !text.trim()}
                        aria-label="Send message"
                        className="w-11 h-11 rounded-full bg-[#3E9E2E] hover:bg-[#2E7E22] disabled:opacity-40 text-white flex items-center justify-center transition-colors shrink-0"
                        data-testid="messages-send"
                    >
                        <Send size={18} strokeWidth={2} />
                    </button>
                </div>
            </form>
        </div>
    );
}
